import { Meal, MealNote, NutritionalTarget, Tramsformed_Meal } from "./mealPlan.types";
import { FINISHED_MEAL_STATUSES, MEAL_SCHEDULE, NOTE_STATUS } from './constants';
import { areSameDay, isDateInPast } from "./Calendar/utils";


export const transformMeals = (mealTypes: string[], meals?: Meal[], notes?: MealNote[]) => {
    if ((!meals || meals.length === 0) && (!notes || notes.length === 0)) {
        return null;
    }

    return mealTypes
        .map((type) => {
            const mealsOfType = meals ? meals.filter((meal) => meal.meal_type === type) : [];
            const notesOfType = notes ? notes.filter((note) => note.meal_type === type) : [];
            return {
                typeName: type,
                meals: mealsOfType,
                notes: notesOfType,
            };
        })
        .filter((group) => group.meals.length > 0 || group.notes.length > 0);
};

export const getMissingMealTypes = (mealTypes: string[], meals?: Meal[], notes?: MealNote[]) => {
    if ((!meals || meals.length === 0) && (!notes || notes.length === 0)) {
        return mealTypes;
    }

    const existingMealTypes = new Set([
        ...(meals ? meals.map((meal: Meal) => meal.meal_type) : []),
        ...(notes ? notes.map((note: MealNote) => note.meal_type) : [])
    ]);
    return mealTypes.filter(type => !existingMealTypes.has(type));
};

export const getMealTime = (mealType: string, date: Date): Date => {
    const newDate = new Date(date);

    const meal = MEAL_SCHEDULE.find(meal => meal.mealType.toLowerCase() === mealType.toLowerCase());

    if (!meal) {
        console.warn('Unknown meal type:', mealType);
        return newDate;
    }

    newDate.setHours(meal.time.hour, meal.time.minute, 0, 0);
    return newDate;
};

export const getNextMealTime = (currentMealType: string, mealDate: Date): Date => {
    const nextMealDate = new Date(mealDate);

    const currentMealIndex = MEAL_SCHEDULE.findIndex(
        meal => meal.mealType.toLowerCase() === currentMealType.toLowerCase()
    );

    if (currentMealIndex === -1) {
        console.warn('Unknown meal type:', currentMealType);
        return nextMealDate;
    }

    const isLastMeal = currentMealIndex === MEAL_SCHEDULE.length - 1;

    if (isLastMeal) {
        // Move to the next day for the first meal (breakfast) of the following day
        nextMealDate.setDate(nextMealDate.getDate() + 1);
        const firstMeal = MEAL_SCHEDULE[0];
        nextMealDate.setHours(firstMeal.time.hour, firstMeal.time.minute, 0, 0);
    } else {
        const upcomingMeal = MEAL_SCHEDULE[currentMealIndex + 1];
        nextMealDate.setHours(upcomingMeal.time.hour, upcomingMeal.time.minute, 0, 0);
    }

    return nextMealDate;
};

export const isPastNextMeal = (mealType: string, mealDate: string): boolean => {
    const currentDate = new Date();
    const mealDateNew = new Date(mealDate + "Z");
    const nextMealTime = getNextMealTime(mealType, mealDateNew);
    return nextMealTime.getTime() < currentDate.getTime();
};

export const getCurrentAndFutureDate = () => {
    const currentDate = new Date();
    const futureDate = new Date();

    futureDate.setDate(currentDate.getDate() + 6);

    const formatDate = (date: Date) => date.toLocaleString('en-US', { month: 'short', day: '2-digit' });

    return {
        currentDate: formatDate(currentDate),
        futureDate: formatDate(futureDate)
    };
}

export const getTotalCalories = (meals: Meal[], notes?: MealNote[]) => {
    const mealsCalories = meals.reduce((totalCalories, meal) => {
        if (meal?.Recipes_E3?.Recipe_Variations && meal.Recipes_E3.Recipe_Variations[0].metadata?.nutritionalValues?.calories) {
            const calories = meal?.Recipes_E3?.Recipe_Variations[0]?.metadata?.nutritionalValues?.calories;
            if (calories) {
                return totalCalories + Number(calories);
            }
        }
        return totalCalories;
    }, 0);

    const notesCalories = notes ? notes.reduce((totalCalories, note) => {
        const calories = note.content?.nutritionalInfo?.calories;
        if (calories && calories > 0) {
            return totalCalories + Number(calories);
        }
        return totalCalories;
    }, 0) : 0;

    return mealsCalories + notesCalories;
};

export const isDateInRange = (date: Date, startDate: Date, endDate: Date) => {
    const clearTime = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const clearedDate = clearTime(date);
    const clearedStartDate = clearTime(startDate);
    const clearedEndDate = clearTime(endDate);

    return clearedDate >= clearedStartDate && clearedDate <= clearedEndDate;
};

export const getTotalNutritionalInfo = (transformedMeals: Tramsformed_Meal[] | null, nutritionalTarget?: NutritionalTarget) => {
    if (!transformedMeals) {
        return [
            {
                label: "calories",
                value: 0,
                maxValue: nutritionalTarget?.calories || 1,
                unit: "kCal"
            },
            {
                label: "protein",
                value: 0,
                maxValue: nutritionalTarget?.protein || 1,
                unit: "g"
            },
            {
                label: "carbs",
                value: 0,
                maxValue: nutritionalTarget?.carbs || 1,
                unit: "g"
            },
            {
                label: "fat",
                value: 0,
                maxValue: nutritionalTarget?.fat || 1,
                unit: "g"
            }
        ];
    }

    const initialNutrition = {
        calories: 0,
        protein: 0,
        carbs: 0,
        fat: 0,
    };

    const totalNutrition = transformedMeals.reduce((totalNutrition, mealData) => {
        const { meals, notes } = mealData;

        meals.forEach((meal) => {
            if (FINISHED_MEAL_STATUSES.includes(meal.status)) {
                const nutrition = meal?.Recipes_E3?.Recipe_Variations[0]?.metadata?.nutritionalValues;
                if (nutrition) {
                    totalNutrition.calories += Number(nutrition.calories || 0);
                    totalNutrition.protein += Number(nutrition.protein || 0);
                    totalNutrition.carbs += Number(nutrition.carbs || 0);
                    totalNutrition.fat += Number(nutrition.fat || 0);
                }
            }
        });

        notes?.forEach((note) => {
            if (note.status === NOTE_STATUS.COMPLETED) {
                const nutrition = note.content?.nutritionalInfo;
                if (nutrition && nutrition.calories > 0) {
                    totalNutrition.calories += Number(nutrition.calories || 0);
                    totalNutrition.protein += Number(nutrition.protein || 0);
                    totalNutrition.carbs += Number(nutrition.carbs || 0);
                    totalNutrition.fat += Number(nutrition.fat || 0);
                }
            }
        });

        return totalNutrition;
    }, initialNutrition);

    return [
        {
            label: "calories",
            value: totalNutrition?.calories ?? 0,
            maxValue: nutritionalTarget?.calories || 0,
            unit: "kCal"
        },
        {
            label: "protein",
            value: totalNutrition?.protein ?? 0,
            maxValue: nutritionalTarget?.protein || 0,
            unit: "g"
        },
        {
            label: "carbs",
            value: totalNutrition?.carbs ?? 0,
            maxValue: nutritionalTarget?.carbs || 0,
            unit: "g"
        },
        {
            label: "fat",
            value: totalNutrition?.fat ?? 0,
            maxValue: nutritionalTarget?.fat || 0,
            unit: "g"
        }
    ];
};


export const formatTime = (time: number): string | null => {
    if (!time)
        return null

    const hr = Math.floor(time / 60);
    const min = time % 60;

    return time > 60
        ? `${hr}h ${min || ''}${min ? 'm' : ''}`
        : `${time} m`;
};

export const increaseServingSize = (currentSize: number, maxServing: number) => {
    return Math.min(currentSize + 1, maxServing);
};

export const decreaseServingSize = (currentSize: number, minServing: number) => {
    return Math.max(currentSize - 1, minServing);
};

export const formatMealDate = (mealType: string, selectedDate: Date): string => {
    if (!mealType || typeof mealType !== 'string') {
        return '';
    }

    const date = new Date(selectedDate);
    if (isNaN(date.getTime())) {
        return '';
    }

    const options: Intl.DateTimeFormatOptions = {
        weekday: 'short',
        day: '2-digit',
        month: 'short'
    };

    const formattedDate = date.toLocaleDateString('en-US', options);

    const capitalizedMealType = mealType.charAt(0).toUpperCase() + mealType.slice(1).toLowerCase();

    return `${capitalizedMealType} on ${formattedDate}`;
};

export const capitalizeMealType = (mealType: string): string => {
    if (!mealType || typeof mealType !== 'string') {
        return '';
    }
    return mealType.charAt(0).toUpperCase() + mealType.slice(1).toLowerCase();
};

export const sortMealsByRecipeName = (meals?: Meal[]) => {
    if (!meals) {
        return [];
    }

    return [...meals].sort((a, b) =>
        (a?.Recipes_E3?.name?.toLowerCase() || '').localeCompare(b?.Recipes_E3?.name?.toLowerCase() || '')
    );
};

export const sortNotesByName = (notes?: MealNote[]) => {
    if (!notes) {
        return [];
    }

    return [...notes]
        .filter(note => note?.content?.name)
        .sort((a, b) =>
            a.content.name.toLowerCase().localeCompare(b.content.name.toLowerCase())
        );
};


export const MEAL_STATUSES = {
    PAST: 'PAST',
    CURRENT: 'CURRENT',
    FUTURE: 'FUTURE',
    NOT_FOUND: 'NOT_FOUND'
};

export const getMealStatus = (mealType: string, date: Date): string => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const isToday = date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    // Find the index of the specified meal type
    const mealIndex = MEAL_SCHEDULE.findIndex(meal => meal.mealType === mealType);
    if (mealIndex === -1) return MEAL_STATUSES.NOT_FOUND;

    // If the date is not today and is in the future, return FUTURE
    if (!isToday && date > now) {
        return MEAL_STATUSES.FUTURE;
    }

    // If the date is in the past, return PAST
    if (!isToday && date < now) {
        return MEAL_STATUSES.PAST;
    }

    // Calculate time in minutes for the specified (current) meal
    const currentMealMinutes = MEAL_SCHEDULE[mealIndex].time.hour * 60 + MEAL_SCHEDULE[mealIndex].time.minute;

    // Check if the meal time is in the past
    if (currentMinutes > currentMealMinutes) {
        return MEAL_STATUSES.PAST;
    }

    // Check if it's the next upcoming meal
    let isNextMeal = false;

    if (mealIndex === 0) {
        isNextMeal = true; // The first meal of the day is considered the next if it's not past
    } else {
        const previousMealTime = MEAL_SCHEDULE[mealIndex - 1].time;
        const previousMealMinutes = previousMealTime.hour * 60 + previousMealTime.minute;
        isNextMeal = currentMinutes >= previousMealMinutes && currentMinutes < currentMealMinutes;
    }

    return isNextMeal ? MEAL_STATUSES.CURRENT : MEAL_STATUSES.FUTURE;
};

//  returns next date if current time is past the time for last meal of the day 
export const getAdjustedDateTime = (): Date => {
    const currentDateTime = new Date();
    const currentTotalMinutes = currentDateTime.getHours() * 60 + currentDateTime.getMinutes();

    // Get the time in minutes for the last meal of the day
    const lastMealOfDay = MEAL_SCHEDULE[MEAL_SCHEDULE.length - 1];
    const lastMealTotalMinutes = lastMealOfDay.time.hour * 60 + lastMealOfDay.time.minute;

    // If the current time is after the last meal time, return the next day at 00:00:00
    if (currentTotalMinutes > lastMealTotalMinutes) {
        const nextDayMidnight = new Date(currentDateTime);
        nextDayMidnight.setDate(currentDateTime.getDate() + 1);
        nextDayMidnight.setHours(0, 0, 0, 0); // Set to 00:00:00
        return nextDayMidnight;
    }

    // Otherwise, return the current date and time
    return currentDateTime;
};

export const filterMealsByDate = (selectedDate: Date, meals?: Meal[]) => {
    if (!meals || meals.length === 0) {
        return [];
    }
    return meals?.filter((meal) => {
        const isSameDate = areSameDay(meal.scheduled_date_time, selectedDate);

        return isSameDate;
    });
};

export const filterNotesByDate = (selectedDate: Date, notes?: MealNote[]) => {
    if (!notes || notes.length === 0) {
        return [];
    }
    return notes?.filter((note) => {
        const isSameDate = areSameDay(note.scheduled_date_time, selectedDate);
        return isSameDate;
    });
};

export const isRecipeInMeals = (meals: Meal[], recipeId: string): boolean => {
    if (!meals || !meals.length || !recipeId)
        return false;
    return meals.some(meal => meal.recipe_id === recipeId);
};


export const getDatesFromNow = (numberOfDays: number) => {
    const gte = new Date();
    gte.setHours(0, 0, 0, 0);

    const lte = new Date(gte);
    lte.setDate(gte.getDate() + numberOfDays);
    lte.setHours(23, 59, 59, 999);

    return { gteCalculated: gte, lteCalculated: lte };
};


export const formatCustomDate = (date: any) => {
    const datePart = date.toISOString().split('T')[0];
  
    const timePart = date.toTimeString().split(' ')[0];
  
    const offsetMinutes = -date.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
  
    const offsetSign = offsetMinutes >= 0 ? '+' : '-';
    const offset = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMins).padStart(2, '0')}`;
  
    return `${datePart}Z${timePart}${offset}`;
  }